export interface CreativeLearningOperationsReport {
  reportId: string;
  projectId: string;
  window: { from: string; to: string };
  completedExperiments: number;
  experimentThroughputPer30Days: number;
  promotedFindingCount: number;
  replicationAttemptCount: number;
  passedReplicationCount: number;
  replicationCoverage: number | null;
  replicationPassRate: number | null;
  decisionInformingFindingCount: number;
  learningYield: number | null;
  decisionsWithMatureCounterfactual: number;
  regrettedDecisionCount: number;
  decisionRegretRate: number | null;
  totalRegretContributionMinor: number | null;
  totalRegretCurrency: string | null;
  totalRegretCurrencyExponent: number | null;
  totalRegretValueBasis: 'contribution' | null;
  regretContributionByCurrency: Array<{
    currency: string;
    currencyExponent: number;
    valueBasis: 'contribution';
    amountMinor: number;
    outcomeCount: number;
  }>;
  unavailableReasonCodes: string[];
  sourceOutcomeIds: string[];
  contentHash: string;
  asOf: string;
  providerMutationCount: 0;
}

export function parseCreativeLearningOperationsReport(value: unknown): CreativeLearningOperationsReport {
  const row = object(value, 'learning report');
  exactKeys(row, [
    'reportId', 'projectId', 'window', 'completedExperiments', 'experimentThroughputPer30Days',
    'promotedFindingCount', 'replicationAttemptCount', 'passedReplicationCount',
    'replicationCoverage', 'replicationPassRate', 'decisionInformingFindingCount', 'learningYield',
    'decisionsWithMatureCounterfactual', 'regrettedDecisionCount', 'decisionRegretRate',
    'totalRegretContributionMinor', 'totalRegretCurrency', 'totalRegretCurrencyExponent',
    'totalRegretValueBasis', 'regretContributionByCurrency', 'unavailableReasonCodes',
    'sourceOutcomeIds', 'contentHash', 'asOf', 'providerMutationCount',
  ], 'learning report');
  const window = object(row.window, 'learning report window');
  exactKeys(window, ['from', 'to'], 'learning report window');
  const regretContributionByCurrency = array(row.regretContributionByCurrency, 'regretContributionByCurrency')
    .map((entry, index) => regretBucket(entry, `regretContributionByCurrency[${index}]`));
  const parsed: CreativeLearningOperationsReport = {
    reportId: token(row.reportId, 'reportId'),
    projectId: token(row.projectId, 'projectId'),
    window: {
      from: timestamp(window.from, 'window.from'),
      to: timestamp(window.to, 'window.to'),
    },
    completedExperiments: count(row.completedExperiments, 'completedExperiments'),
    experimentThroughputPer30Days: nonNegative(row.experimentThroughputPer30Days, 'experimentThroughputPer30Days'),
    promotedFindingCount: count(row.promotedFindingCount, 'promotedFindingCount'),
    replicationAttemptCount: count(row.replicationAttemptCount, 'replicationAttemptCount'),
    passedReplicationCount: count(row.passedReplicationCount, 'passedReplicationCount'),
    replicationCoverage: nullableRate(row.replicationCoverage, 'replicationCoverage'),
    replicationPassRate: nullableRate(row.replicationPassRate, 'replicationPassRate'),
    decisionInformingFindingCount: count(row.decisionInformingFindingCount, 'decisionInformingFindingCount'),
    learningYield: nullableNonNegative(row.learningYield, 'learningYield'),
    decisionsWithMatureCounterfactual: count(
      row.decisionsWithMatureCounterfactual,
      'decisionsWithMatureCounterfactual',
    ),
    regrettedDecisionCount: count(row.regrettedDecisionCount, 'regrettedDecisionCount'),
    decisionRegretRate: nullableRate(row.decisionRegretRate, 'decisionRegretRate'),
    totalRegretContributionMinor: nullableInteger(
      row.totalRegretContributionMinor,
      'totalRegretContributionMinor',
    ),
    totalRegretCurrency: nullableCurrency(row.totalRegretCurrency, 'totalRegretCurrency'),
    totalRegretCurrencyExponent: nullableCurrencyExponent(
      row.totalRegretCurrencyExponent,
      'totalRegretCurrencyExponent',
    ),
    totalRegretValueBasis: nullableContribution(row.totalRegretValueBasis, 'totalRegretValueBasis'),
    regretContributionByCurrency,
    unavailableReasonCodes: tokenArray(row.unavailableReasonCodes, 'unavailableReasonCodes'),
    sourceOutcomeIds: tokenArray(row.sourceOutcomeIds, 'sourceOutcomeIds'),
    contentHash: sha256(row.contentHash, 'contentHash'),
    asOf: timestamp(row.asOf, 'asOf'),
    providerMutationCount: zero(row.providerMutationCount, 'providerMutationCount'),
  };
  if (Date.parse(parsed.window.from) >= Date.parse(parsed.window.to)) {
    throw new Error('learning report window is invalid');
  }
  if (parsed.passedReplicationCount > parsed.replicationAttemptCount
    || parsed.regrettedDecisionCount > parsed.decisionsWithMatureCounterfactual) {
    throw new Error('learning report counts are inconsistent');
  }
  const totalTuple = [
    parsed.totalRegretContributionMinor,
    parsed.totalRegretCurrency,
    parsed.totalRegretCurrencyExponent,
    parsed.totalRegretValueBasis,
  ];
  if (totalTuple.some((entry) => entry !== null) && totalTuple.some((entry) => entry === null)) {
    throw new Error('learning report total regret basis is incomplete');
  }
  if (new Set(regretContributionByCurrency.map((bucket) => (
    `${bucket.currency}:${bucket.currencyExponent}:${bucket.valueBasis}`
  ))).size !== regretContributionByCurrency.length) {
    throw new Error('learning report regret currency buckets are duplicated');
  }
  if (parsed.totalRegretContributionMinor !== null) {
    const only = regretContributionByCurrency.length === 1 ? regretContributionByCurrency[0] : null;
    if (!only || only.amountMinor !== parsed.totalRegretContributionMinor
      || only.currency !== parsed.totalRegretCurrency
      || only.currencyExponent !== parsed.totalRegretCurrencyExponent
      || only.valueBasis !== parsed.totalRegretValueBasis) {
      throw new Error('learning report homogeneous regret total does not match its currency bucket');
    }
  }
  if (parsed.sourceOutcomeIds.length
    !== parsed.completedExperiments + parsed.replicationAttemptCount) {
    throw new Error('learning report outcome lineage is incomplete');
  }
  assertRate(parsed.replicationPassRate, parsed.passedReplicationCount, parsed.replicationAttemptCount, 'replicationPassRate');
  assertRate(parsed.decisionRegretRate, parsed.regrettedDecisionCount, parsed.decisionsWithMatureCounterfactual, 'decisionRegretRate');
  if ((parsed.promotedFindingCount === 0) !== (parsed.replicationCoverage === null)) {
    throw new Error('replicationCoverage denominator is inconsistent');
  }
  if ((parsed.completedExperiments === 0) !== (parsed.learningYield === null)) {
    throw new Error('learningYield denominator is inconsistent');
  }
  return parsed;
}

function object(value: unknown, field: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(`${field} is invalid`);
  return value as Record<string, unknown>;
}

function token(value: unknown, field: string): string {
  if (typeof value !== 'string' || value.length < 1 || value.length > 512
    || value !== value.trim() || /[\u0000-\u001f\u007f]/u.test(value)) throw new Error(`${field} is invalid`);
  return value;
}

function tokenArray(value: unknown, field: string): string[] {
  if (!Array.isArray(value) || value.length > 1_000) throw new Error(`${field} is invalid`);
  const parsed = value.map((entry, index) => token(entry, `${field}[${index}]`));
  if (new Set(parsed).size !== parsed.length) throw new Error(`${field} contains duplicates`);
  return parsed.sort();
}

function array(value: unknown, field: string): unknown[] {
  if (!Array.isArray(value) || value.length > 1_000) throw new Error(`${field} is invalid`);
  return value;
}

function finite(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) throw new Error(`${field} is invalid`);
  return value;
}

function nonNegative(value: unknown, field: string): number {
  const parsed = finite(value, field);
  if (parsed < 0) throw new Error(`${field} is invalid`);
  return parsed;
}

function count(value: unknown, field: string): number {
  const parsed = nonNegative(value, field);
  if (!Number.isSafeInteger(parsed)) throw new Error(`${field} is invalid`);
  return parsed;
}

function nullableRate(value: unknown, field: string): number | null {
  if (value === null) return null;
  const parsed = finite(value, field);
  if (parsed < 0 || parsed > 1) throw new Error(`${field} is invalid`);
  return parsed;
}

function nullableNonNegative(value: unknown, field: string): number | null {
  return value === null ? null : nonNegative(value, field);
}

function nullableInteger(value: unknown, field: string): number | null {
  if (value === null) return null;
  const parsed = finite(value, field);
  if (!Number.isSafeInteger(parsed) || parsed < 0) throw new Error(`${field} is invalid`);
  return parsed;
}

function nullableCurrency(value: unknown, field: string): string | null {
  if (value === null) return null;
  if (typeof value !== 'string' || !/^[A-Z]{3}$/u.test(value)) throw new Error(`${field} is invalid`);
  return value;
}

function nullableCurrencyExponent(value: unknown, field: string): number | null {
  if (value === null) return null;
  const parsed = count(value, field);
  if (parsed > 6) throw new Error(`${field} is invalid`);
  return parsed;
}

function nullableContribution(value: unknown, field: string): 'contribution' | null {
  if (value === null) return null;
  if (value !== 'contribution') throw new Error(`${field} is invalid`);
  return value;
}

function regretBucket(value: unknown, field: string): CreativeLearningOperationsReport['regretContributionByCurrency'][number] {
  const row = object(value, field);
  exactKeys(row, ['currency', 'currencyExponent', 'valueBasis', 'amountMinor', 'outcomeCount'], field);
  const currency = nullableCurrency(row.currency, `${field}.currency`);
  const currencyExponent = nullableCurrencyExponent(row.currencyExponent, `${field}.currencyExponent`);
  const valueBasis = nullableContribution(row.valueBasis, `${field}.valueBasis`);
  if (currency === null || currencyExponent === null || valueBasis === null) throw new Error(`${field} is incomplete`);
  const outcomeCount = count(row.outcomeCount, `${field}.outcomeCount`);
  if (outcomeCount < 1) throw new Error(`${field}.outcomeCount is invalid`);
  return {
    currency,
    currencyExponent,
    valueBasis,
    amountMinor: count(row.amountMinor, `${field}.amountMinor`),
    outcomeCount,
  };
}

function assertRate(value: number | null, numerator: number, denominator: number, field: string): void {
  if (denominator === 0) {
    if (value !== null) throw new Error(`${field} must be unavailable without a denominator`);
    return;
  }
  if (value === null || Math.abs(value - numerator / denominator) > 1e-12) {
    throw new Error(`${field} does not match its counts`);
  }
}

function timestamp(value: unknown, field: string): string {
  const parsed = typeof value === 'string' ? Date.parse(value) : Number.NaN;
  if (!Number.isFinite(parsed)) throw new Error(`${field} is invalid`);
  return new Date(parsed).toISOString();
}

function sha256(value: unknown, field: string): string {
  const parsed = token(value, field);
  if (!/^[a-f0-9]{64}$/u.test(parsed)) throw new Error(`${field} is invalid`);
  return parsed;
}

function zero(value: unknown, field: string): 0 {
  if (value !== 0) throw new Error(`${field} must be zero`);
  return 0;
}

function exactKeys(value: Record<string, unknown>, allowed: readonly string[], field: string): void {
  const unknown = Object.keys(value).filter((key) => !allowed.includes(key));
  if (unknown.length) throw new Error(`${field} contains unsupported fields: ${unknown.sort().join(', ')}`);
}
